// Image Generator Tool - Main Tool Implementation

import { BaseTool } from '../../core/BaseTool';
import { ColorUtils } from '../../utils/colorUtils';
import { ColorPicker } from '../../ui/colorPicker';
import { FormatSelector } from '../../ui/formatSelector';
import { CookieConsentController } from '../../services/cookieConsentController';
import { Analytics } from '../../services/analytics';
import { Modal } from '../../ui/modal';
import { AppIcon } from './appIcon';
import { TaglineManager } from './taglineManager';
import { ImageRenderer } from './imageRenderer';
import { UIController } from './uiController';
import { PreviewManager } from './previewManager';
import { ImageUploadManager } from './imageUploadManager';
import { ExportManager } from './exportManager';
import type { RenderConfig } from '../../types';

// Extend Window interface for global references
declare global {
  interface Window {
    appModal?: Modal;
    appIcon?: AppIcon;
    taglineManager?: TaglineManager;
    analytics?: Analytics;
    cookieConsent?: CookieConsentController;
    imageGeneratorTool?: ImageGeneratorTool;
    ColorUtils?: typeof ColorUtils;
    gtag?: (...args: any[]) => void;
  }
}

// Make ColorUtils available globally for backward compatibility
window.ColorUtils = ColorUtils;

/**
 * Image Generator Tool Class
 * Extends BaseTool and coordinates all modules for image generation
 */
export class ImageGeneratorTool extends BaseTool {
  private cookieConsent: CookieConsentController;
  private analytics: Analytics;
  private modal: Modal;
  private appIcon: AppIcon;
  private taglineManager: TaglineManager;
  private imageRenderer: ImageRenderer;
  private uiController: UIController;
  private previewManager: PreviewManager;
  private imageUploadManager: ImageUploadManager;
  private exportManager: ExportManager;
  private colorPicker: ColorPicker;
  private formatSelector: FormatSelector;

  constructor() {
    super();
    
    // Initialize cookie consent system first
    this.cookieConsent = new CookieConsentController();

    // Initialize all modules
    this.analytics = new Analytics();
    this.modal = new Modal();
    this.appIcon = new AppIcon(this.modal);
    this.taglineManager = new TaglineManager();
    this.imageRenderer = new ImageRenderer('canvas');
    this.uiController = new UIController(this.analytics);
    this.previewManager = new PreviewManager(this.imageRenderer.canvas);
    this.imageUploadManager = new ImageUploadManager(this.analytics);
    this.exportManager = new ExportManager(this.imageRenderer, this.analytics);

    // Store references globally for backward compatibility and analytics tracking
    window.appModal = this.modal;
    window.appIcon = this.appIcon;
    window.taglineManager = this.taglineManager;
    window.analytics = this.analytics;
    window.cookieConsent = this.cookieConsent;
    window.imageGeneratorTool = this;

    // Initialize color picker
    this.colorPicker = new ColorPicker();
    this.colorPicker.setOnColorChange((color: string, preview: HTMLElement) => {
      const colorType = preview.classList.contains('bg-preview') ? 'background' : 'text';
      this.uiController.updateColorPreview(colorType as 'background' | 'text', color);
      this.updatePreview();
    });

    // Initialize format selector
    this.formatSelector = new FormatSelector();
  }

  /**
   * Get the tool name
   */
  getToolName(): string {
    return 'Image Generator';
  }

  /**
   * Initialize the tool
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    // Initialize format selector
    this.formatSelector.init(
      'formatSelectorContainer',
      ['png', 'jpeg', 'webp'],
      'png',
      (format) => {
        this.uiController.setCurrentFormat(format as any);
        this.analytics.trackFormatSelection(format as any);
        // Update format info display
        const formatInfo = document.getElementById('formatInfo');
        if (formatInfo) {
          formatInfo.textContent = `${format.toUpperCase()} Format`;
        }
      }
    );

    this.bindEvents();
    this.initializeUI();
    this.analytics.trackAppInitialized();
    
    this.setInitialized(true);
  }

  /**
   * Initialize UI elements
   */
  private initializeUI(): void {
    this.uiController.initializeColorPreviews();
    this.previewManager.initializePreview();
    this.updatePreview();
  }

  /**
   * Bind all event listeners
   */
  private bindEvents(): void {
    this.bindFormEvents();
    this.bindButtonEvents();
    this.bindDragDropEvents();
  }

  /**
   * Bind form input events
   */
  private bindFormEvents(): void {
    // Text content
    const textInput = document.getElementById('textContent') as HTMLTextAreaElement;
    textInput?.addEventListener('input', () => this.updatePreview());

    // Background color
    const bgInput = document.getElementById('backgroundColorText') as HTMLInputElement;
    bgInput?.addEventListener('input', () => {
      this.uiController.updateColorPreview('background', bgInput.value);
      this.updatePreview();
    });

    // Text color
    const colorInput = document.getElementById('textColorText') as HTMLInputElement;
    colorInput?.addEventListener('input', () => {
      this.uiController.updateColorPreview('text', colorInput.value);
      this.updatePreview();
    });

    // Size inputs
    const widthInput = document.getElementById('widthInput') as HTMLInputElement;
    const heightInput = document.getElementById('heightInput') as HTMLInputElement;
    widthInput?.addEventListener('input', () => this.updatePreview());
    heightInput?.addEventListener('input', () => this.updatePreview());

    // Font size
    const fontSizeInput = document.getElementById('fontSize') as HTMLInputElement;
    fontSizeInput?.addEventListener('input', () => this.updatePreview());

    // Font family
    const fontFamilyInput = document.getElementById('fontFamily') as HTMLSelectElement;
    fontFamilyInput?.addEventListener('change', () => this.updatePreview());
  }

  /**
   * Bind button events
   */
  private bindButtonEvents(): void {
    // Download button
    const downloadBtn = document.getElementById('generateBtn');
    downloadBtn?.addEventListener('click', () => {
      const config = this.getRenderConfig();
      this.exportManager.generateAndDownload(config);
    });

    // Reset button
    const resetBtn = document.getElementById('clearBtn');
    resetBtn?.addEventListener('click', () => {
      this.resetForm();
    });

    // Color picker palette buttons
    document.querySelectorAll('.palette-button').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const target = e.currentTarget as HTMLElement;
        const colorPreview = target.parentElement?.querySelector('.color-preview') as HTMLElement;
        if (colorPreview) {
          this.colorPicker.open(colorPreview);
        }
      });
    });

    // Also open color picker when clicking the color preview swatch
    document.querySelectorAll('.color-preview').forEach(preview => {
      (preview as HTMLElement).style.cursor = 'pointer';
      preview.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.colorPicker.open(preview as HTMLElement);
      });
    });

    // Initialize format buttons
    document.querySelectorAll('.format-option').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const format = target.dataset.format;
        if (format) {
          this.uiController.selectFormat(format as any);
          this.updatePreview();
        }
      });
    });

    // Initialize tab buttons
    document.querySelectorAll('.tab-button').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const tab = target.dataset.tab;
        if (tab) {
          this.uiController.switchTab(tab as any);
        }
      });
    });
  }

  /**
   * Bind drag and drop events
   */
  private bindDragDropEvents(): void {
    const dropZone = document.getElementById('canvas-container');
    if (!dropZone) return;

    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault();
      dropZone.classList.add('drag-over');
    });

    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('drag-over');
    });

    dropZone.addEventListener('drop', (e) => {
      e.preventDefault();
      dropZone.classList.remove('drag-over');
      
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        this.imageUploadManager.handleImageFile(files[0]);
      }
    });
  }

  /**
   * Get render configuration from form
   */
  private getRenderConfig(): RenderConfig {
    const widthInput = document.getElementById('widthInput') as HTMLInputElement;
    const heightInput = document.getElementById('heightInput') as HTMLInputElement;
    const textInput = document.getElementById('textContent') as HTMLTextAreaElement;
    const bgColorInput = document.getElementById('backgroundColorText') as HTMLInputElement;
    const textColorInput = document.getElementById('textColorText') as HTMLInputElement;
    const fontSizeInput = document.getElementById('fontSize') as HTMLInputElement;
    const fontFamilyInput = document.getElementById('fontFamily') as HTMLSelectElement;
    
    // Normalize format (jpeg -> jpg for consistency)
    let format = this.uiController.currentFormat;
    if (format === 'jpeg') format = 'jpg';
    
    return {
      width: parseInt(widthInput?.value) || 800,
      height: parseInt(heightInput?.value) || 600,
      text: textInput?.value || '',
      backgroundColor: bgColorInput?.value || '#3B82F6',
      textColor: textColorInput?.value || '#111827',
      fontSize: parseInt(fontSizeInput?.value) || 48,
      fontFamily: fontFamilyInput?.value || 'Arial, sans-serif',
      format: format as 'png' | 'jpg' | 'webp'
    };
  }

  /**
   * Update preview with current settings
   */
  private updatePreview(): void {
    const config = this.getRenderConfig();
    this.imageRenderer.renderImage(config);
    this.previewManager.showCanvasPreview();
    this.uiController.updateInfoDisplays(config.width, config.height);

  }

  /**
   * Reset form to defaults
   */
  private resetForm(): void {
    // Reset all inputs to defaults
    const widthInput = document.getElementById('widthInput') as HTMLInputElement;
    const heightInput = document.getElementById('heightInput') as HTMLInputElement;
    const textInput = document.getElementById('textContent') as HTMLTextAreaElement;
    const bgColorInput = document.getElementById('backgroundColorText') as HTMLInputElement;
    const textColorInput = document.getElementById('textColorText') as HTMLInputElement;
    const fontSizeInput = document.getElementById('fontSize') as HTMLInputElement;
    const fontFamilyInput = document.getElementById('fontFamily') as HTMLSelectElement;

    if (widthInput) widthInput.value = '800';
    if (heightInput) heightInput.value = '600';
    if (textInput) textInput.value = '';
    if (bgColorInput) bgColorInput.value = '#3B82F6';
    if (textColorInput) textColorInput.value = '#111827';
    if (fontSizeInput) fontSizeInput.value = '48';
    if (fontFamilyInput) fontFamilyInput.value = 'Arial, sans-serif';

    this.uiController.initializeColorPreviews();
    this.uiController.selectFormat('png');
    this.updatePreview();
    this.analytics.trackFormCleared();
  }

  /**
   * Cleanup and destroy the tool
   */
  destroy(): void {
    // Clean up event listeners and resources
    this.setInitialized(false);
  }
}
